// filepath: apps/web/src/admin/organization-units/api.ts
//
// ISS-517 — typed REST client for /api/directory/organization-units/*
// and the /api/directory/legal-entities picker feed.
//
// Wire shape per `internal/directory/organization_unit/handlers.go`
// (7 mounted routes, handlers.go:40-58) + `package.go:40-52`
// (OrganizationUnit struct). This module does NOT modify the Go side
// — FR-DIR-2 already shipped in master.
//
// THIS MODULE
//   - Routes every call through `apiFetch` (bearer + 401 retry chain).
//     No raw `fetch()` (static-guard contract).
//   - Normalises the Go wire into the flat SPA `OrganizationUnit`
//     shape via `unwireOrganizationUnit`. Unlike the ISS-518 Person
//     client there is NO `value` blob to unwrap: the Go struct's JSON
//     tags ARE the wire keys. What `unwireOrganizationUnit` does is
//     fold the three `,omitempty` Go columns
//     (`parent_organization_unit_id` / `code` / `closed_at`) from
//     ABSENT to `null`, so the flat SPA surface always carries them
//     as `string | null` instead of `undefined`. That keeps INV-2
//     intact (the SPA never synthesises a value the server did not
//     send — it only normalises Go's omitempty encoding of a NULL).
//   - Forwards INV-8 `X-Idempotency-Key` on every mutation via
//     `newIdempotencyKey()`.
//
// INV-2: the unwire never synthesises display fields. Go ships
// `legal_entity_id` only — there is NO `legal_entity_name` on the
// wire and none is invented here. The picker joins names at render
// time from `listLegalEntities()` (BA note_18335 ruling D3).
//
// INV-5: `listLegalEntities()` renders ONLY `name` + `id`. The Go
// legal_entity record also carries `registration_number` /
// `address_sha256` (INV-5 secrets-by-reference-only); this client
// projects them away at the boundary so no downstream component can
// accidentally read them.
//
// INV-7: every mutation carries `row_version` (OCC). The server
// returns 409 + code='row_version_mismatch' on stale submission, and
// 409 + code='cycle_detected' on an illegal reparent
// (`handlers.go:325-338`).

import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";
import type { OrganizationUnit } from "@/features/directory/types";

/**
 * Wire shape of an OrganizationUnit record as Go emits it.
 * Structurally identical to the SPA `OrganizationUnit` type — kept
 * as a distinct local alias so the drift guard in
 * `apps/web/src/directory/__fixtures__/organization_units.test.ts`
 * stays the single compile-time authority on the shape, and so the
 * two nullable wire keys stay honest about being ABSENT on the wire
 * (Go `,omitempty`) rather than `null`.
 */
interface OrganizationUnitWire {
  id: string;
  tenant_id: string;
  legal_entity_id: string;
  parent_organization_unit_id?: string | null;
  name: string;
  code?: string | null;
  status: "active" | "closed";
  closed_at?: string | null;
  row_version: number;
  created_at: string;
  created_by_subject: string;
}

/**
 * List envelope. FR-DIR-2 calls
 * `authorization.WriteStrippedList(..., "items")`
 * (`handlers.go:281`), which emits `{"items": [...], "total": N}`
 * (`internal/directory/authorization/strip.go:317-343`). The
 * `records` key is the WriteStrippedList DEFAULT that the ISS-515 /
 * ISS-516 / ISS-518 sibling entities use; org units do NOT use it.
 * Both are accepted defensively so a server-side envelope rename
 * degrades to an empty list rather than a crash.
 */
interface OrganizationUnitListEnvelope {
  items?: OrganizationUnitWire[];
  records?: OrganizationUnitWire[];
  total?: number;
}

/** Legal-entity list envelope — same dual-key defensive shape. */
interface LegalEntityListEnvelope {
  items?: LegalEntityWire[];
  records?: LegalEntityWire[];
  total?: number;
}

/**
 * The ONLY two legal-entity fields the picker is allowed to see.
 *
 * The Go record (`internal/directory/legal_entity/package.go:55-72`)
 * also emits `jurisdiction_country`, `registration_number`,
 * `tax_id`, `address_sha256`, `created_by_subject`, `removed_at` and
 * `removed_by_subject`. None of them reach the SPA here: BA note
 * 18335 (ruling D3) scopes the picker to "name + id", and INV-5
 * (secrets by reference only) forbids propagating `address_sha256`
 * and `tax_id` into a component tree.
 *
 * DELIBERATE NARROWING. The SPA's own `LegalEntity` interface
 * (features/directory/types.ts) is NOT the Go wire — it declares
 * `jurisdiction` / `updated_at` (which Go does not emit) and omits
 * `jurisdiction_country` / `created_by_subject` (which Go does).
 * That is a real, separate drift, but re-aligning it is OUT OF SCOPE
 * for ISS-517 (BA ruling D1 covers OrganizationUnit only) and would
 * ripple into every LegalEntity consumer. Rather than cast the Go
 * record into the drifted SPA type and inherit its silent-empty bug,
 * this client returns only the two fields the picker renders, so a
 * drift in either type cannot leak into the form.
 */
export interface LegalEntityOption {
  id: string;
  name: string;
}

/** Raw legal-entity wire record (subset this client reads). */
interface LegalEntityWire {
  id?: string;
  name?: string;
}

/** Input for `createOrganizationUnit` (no id / row_version / audit). */
export interface CreateOrganizationUnitInput {
  /**
   * Required by the Go create handler (`handlers.go:63` reads
   * `legal_entity_id` as a non-pointer uuid.UUID). Per BA note 18335
   * (ruling D3) this is a USER-SELECTABLE business field, not an
   * INV-1 security scope: the list view omits the legal-entity filter
   * and shows all tenant units.
   */
  legal_entity_id: string;
  /** Optional parent. null / absent = the new unit is a root. */
  parent_organization_unit_id?: string | null;
  /** Required — Go `Validate(name, code)` rejects a blank name. */
  name: string;
  /** Optional operator code. Omitted when blank (Go NULLIF($6, '')). */
  code?: string | null;
}

/** Input for `updateOrganizationUnit` — INV-7 row_version mandatory. */
export interface UpdateOrganizationUnitInput {
  name?: string;
  code?: string | null;
  /**
   * Explicitly clear the code column. Go `Validate`
   * (`package.go:77-85`) rejects a PRESENT-but-empty code, so a
   * cleared field MUST travel as `clear_code: true` with `code`
   * omitted — never as `code: ""`.
   */
  clear_code?: boolean;
  row_version: number;
}

/**
 * Input for `moveOrganizationUnit`. The dedicated
 * `POST /api/directory/organization-units/{id}/move` route is the
 * ONLY way to reparent: `PATCH /{id}` (handlers.go:110-115) reads
 * just `row_version` / `name` / `code` / `clear_code`.
 */
export interface MoveOrganizationUnitInput {
  /** The new parent. null = promote to root. */
  parent_organization_unit_id: string | null;
  row_version: number;
}

/** Input for `archiveOrganizationUnit` — the Go `close` route. */
export interface ArchiveOrganizationUnitInput {
  row_version: number;
}

/** Query opts for `listOrganizationUnits`. */
export interface ListOrganizationUnitsOpts {
  /** Per BA ruling D3 the list OMITS the legal-entity filter. */
  include_closed?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Generate a v4 UUID via crypto.randomUUID(). Mirrors the
 * counterparties / locations / people API-client idiom.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function callApi<T>(
  url: string,
  opts: {
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const resp = await apiFetch(url, {
    method: opts.method ?? "GET",
    init,
    idempotencyKey: opts.idempotencyKey,
  });
  if (!resp.ok) {
    let code = "";
    let message = `HTTP ${resp.status}`;
    try {
      const errBody = (await resp.json()) as {
        code?: string;
        message?: string;
        error?: string;
      };
      if (typeof errBody.code === "string") code = errBody.code;
      if (typeof errBody.message === "string") message = errBody.message;
      else if (typeof errBody.error === "string") message = errBody.error;
    } catch {
      /* not JSON, fall back to the status string */
    }
    throw new ApiError(message, resp.status, code);
  }
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  return (await resp.json()) as T;
}

const BASE = "/api/directory/organization-units";
const LEGAL_ENTITY_BASE = "/api/directory/legal-entities";

/**
 * The four fields the Go create request struct reads
 * (`internal/directory/organization_unit/handlers.go:62-67`):
 * legal_entity_id (required), parent_organization_unit_id
 * (optional), name (required), code (optional). `pickFields()`
 * forwards ONLY these — `id` / `row_version` / `status` /
 * `closed_at` / `created_at` / `created_by_subject` are
 * server-assigned and must never be forged by the client.
 */
const SLICE1_FORM_FIELDS = [
  "legal_entity_id",
  "parent_organization_unit_id",
  "name",
  "code",
] as const;

function pickFields(
  input: CreateOrganizationUnitInput | UpdateOrganizationUnitInput,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of SLICE1_FORM_FIELDS) {
    const v = (input as unknown as Record<string, unknown>)[k];
    // `undefined` is dropped entirely so Go's `,omitempty` decoders
    // see the field as absent. An explicit `null` is preserved: the
    // Go pointer fields treat absent and null identically, but
    // forwarding null keeps the request body readable in the network
    // log and in the test assertions.
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * Normalise the flat Go wire into the flat SPA `OrganizationUnit`
 * shape.
 *
 * There is NO `value` blob to unwrap here (unlike ISS-518's
 * `unwirePerson`): `internal/directory/organization_unit/package.go:40-52`
 * marshals the struct fields directly, so the wire keys and the SPA
 * keys are identical. The single transformation is folding Go's
 * `,omitempty` encoding of a NULL column into an explicit `null`, so
 * consumers read `string | null` (never `undefined`) — matching the
 * ISS-515 Location / ISS-516 Position precedent.
 *
 * INV-2: no field is invented. `status` is passed through verbatim;
 * a future third Go status surfaces as a widened union on the SPA
 * type rather than being silently coerced here.
 */
function unwireOrganizationUnit(raw: OrganizationUnitWire): OrganizationUnit {
  return {
    id: raw.id,
    tenant_id: raw.tenant_id,
    legal_entity_id: raw.legal_entity_id,
    parent_organization_unit_id: raw.parent_organization_unit_id ?? null,
    name: raw.name,
    code: raw.code ?? null,
    status: raw.status,
    closed_at: raw.closed_at ?? null,
    row_version: raw.row_version,
    created_at: raw.created_at,
    created_by_subject: raw.created_by_subject,
  };
}

/** Project a legal-entity wire record down to the INV-5-safe shape. */
function unwireLegalEntity(raw: LegalEntityWire): LegalEntityOption | null {
  if (typeof raw.id !== "string" || typeof raw.name !== "string") return null;
  return { id: raw.id, name: raw.name };
}

/**
 * GET /api/directory/organization-units?...
 *
 * Per BA ruling D3 the legal-entity filter is intentionally NOT
 * exposed — the list shows all tenant units, each labelled by
 * legal-entity name joined at render time.
 */
export async function listOrganizationUnits(
  opts?: ListOrganizationUnitsOpts,
): Promise<OrganizationUnit[]> {
  const params = new URLSearchParams();
  if (opts?.include_closed) params.set("include_closed", "true");
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  const body = await callApi<
    OrganizationUnitListEnvelope | OrganizationUnitWire[]
  >(url);
  if (Array.isArray(body)) return body.map(unwireOrganizationUnit);
  const records = body?.items ?? body?.records;
  if (!Array.isArray(records)) return [];
  return records.map(unwireOrganizationUnit);
}

/** GET /api/directory/organization-units/{id} — {id} is a path segment. */
export async function getOrganizationUnit(
  id: string,
): Promise<OrganizationUnit> {
  const raw = await callApi<OrganizationUnitWire>(
    `${BASE}/${encodeURIComponent(id)}`,
  );
  return unwireOrganizationUnit(raw);
}

/**
 * POST /api/directory/organization-units — body
 * `{legal_entity_id, parent_organization_unit_id?, name, code?}`
 * (flat — no `fields` wrapper; the Go create struct is flat, unlike
 * the ISS-518 Person create which wraps in `fields`).
 * Returns 201 + the freshly-created record with row_version = 1.
 */
export async function createOrganizationUnit(
  input: CreateOrganizationUnitInput,
  idempotencyKey: string,
): Promise<OrganizationUnit> {
  const raw = await callApi<OrganizationUnitWire>(BASE, {
    method: "POST",
    body: pickFields(input),
    idempotencyKey,
  });
  return unwireOrganizationUnit(raw);
}

/**
 * PATCH /api/directory/organization-units/{id} — body is FLAT
 * `{row_version, name?, code?, clear_code?}` per `handlers.go:110-115`.
 *
 * This route CANNOT reparent: the Go update request has no
 * parent field, and the repository `Update` only writes name / code
 * (`repository.go:113-158`). Reparenting goes exclusively through
 * `moveOrganizationUnit` below (BA ruling D2).
 *
 * Returns 200 + the updated record, or 409 +
 * code='row_version_mismatch' on a stale submission (INV-7).
 */
export async function updateOrganizationUnit(
  id: string,
  input: UpdateOrganizationUnitInput,
  idempotencyKey: string,
): Promise<OrganizationUnit> {
  const body: Record<string, unknown> = { row_version: input.row_version };
  if (input.name !== undefined) body.name = input.name;
  if (input.clear_code) {
    body.clear_code = true;
  } else if (input.code !== undefined && input.code !== null) {
    body.code = input.code;
  }
  const raw = await callApi<OrganizationUnitWire>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: "PATCH", body, idempotencyKey },
  );
  return unwireOrganizationUnit(raw);
}

/**
 * POST /api/directory/organization-units/{id}/move — body is EXACTLY
 * `{row_version, parent_organization_unit_id}` per `handlers.go:151-154`
 * (no `fields` wrapper; `parent_organization_unit_id` has NO
 * `,omitempty`, so an explicit `null` promotes the unit to root).
 *
 * CYCLE SAFETY IS SERVER-AUTHORITATIVE (BA ruling D2). The server
 * runs a recursive CTE (`repository.go:165-203`) and rejects an
 * illegal reparent with HTTP 409 + `code: "cycle_detected"`
 * (`handlers.go:331-332`). This client performs NO recursive
 * descendant walk — it surfaces the server's verdict verbatim.
 *
 * Returns 200 + the reparented record.
 */
export async function moveOrganizationUnit(
  id: string,
  input: MoveOrganizationUnitInput,
  idempotencyKey: string,
): Promise<OrganizationUnit> {
  const raw = await callApi<OrganizationUnitWire>(
    `${BASE}/${encodeURIComponent(id)}/move`,
    {
      method: "POST",
      body: {
        row_version: input.row_version,
        parent_organization_unit_id: input.parent_organization_unit_id,
      },
      idempotencyKey,
    },
  );
  return unwireOrganizationUnit(raw);
}

/**
 * POST /api/directory/organization-units/{id}/close — the archive
 * (soft-delete) verb. The Go handler sets `status='closed'` +
 * `closed_at = now()` and returns 204 No Content
 * (`handlers.go:190-215`, `repository.go:206-222`). It does NOT
 * cascade-close children, and the SPA does not hide them either.
 *
 * INV-7: the caller MUST pass the latest known row_version.
 */
export async function archiveOrganizationUnit(
  id: string,
  input: ArchiveOrganizationUnitInput,
  idempotencyKey: string,
): Promise<void> {
  await callApi<void>(`${BASE}/${encodeURIComponent(id)}/close`, {
    method: "POST",
    body: { row_version: input.row_version },
    idempotencyKey,
  });
}

/**
 * GET /api/directory/legal-entities — the D3 picker feed.
 *
 * INV-5: only `name` + `id` reach the caller. `registration_number`,
 * `tax_id` and `address_sha256` are dropped at the client boundary, so
 * no downstream component can accidentally read them.
 */
export async function listLegalEntities(): Promise<LegalEntityOption[]> {
  const body = await callApi<LegalEntityListEnvelope | LegalEntityWire[]>(
    LEGAL_ENTITY_BASE,
  );
  const records = Array.isArray(body) ? body : (body?.items ?? body?.records);
  if (!Array.isArray(records)) return [];
  return records
    .map((raw) => unwireLegalEntity(raw))
    .filter((o): o is LegalEntityOption => o !== null);
}

/**
 * GET /api/directory/organization-units/{id}/ancestors — the chain of
 * org-unit ids from the given unit up to the root, root-first
 * (`handlers.go:284-306`). Used to render the breadcrumb trail on the
 * detail screen. Returns bare UUIDs — the server emits them
 * unchanged (there is no PII to strip).
 */
export async function getOrganizationUnitAncestors(
  id: string,
): Promise<string[]> {
  const body = await callApi<{ items?: string[] }>(
    `${BASE}/${encodeURIComponent(id)}/ancestors`,
  );
  return Array.isArray(body?.items) ? body.items : [];
}
